import { Link } from 'react-router-dom';
import { css, type Theme } from '@emotion/react';
import { ArrowClockwise, FolderSimple, List, Plus } from '@phosphor-icons/react';
import type { ComponentWithValues } from 'common/stack/ComponentWithValuesDocuments';
import { OnLog } from 'client/state/LogState';
import { SshWarningDropdown } from 'client/pages/deployment/components/SshWarningDropdown';
import { Button } from 'client/components/Button';
import { ListItemText } from 'client/components/Other/ListItemText';
import { Stack } from 'client/components/Stack';
import { Status } from 'client/components/Status/Status';
import { Tag } from 'client/components/Tag';
import { Tooltip } from 'client/components/Tooltip';
import { Typography } from 'client/components/Typography';
import { ComponentIcon } from 'client/sections/components/ComponentIcon';
import type { ComponentStatusTree } from './componentsStatusUtils';
import { useContainerActions } from './useContainerActions';

interface ComponentStatusHeaderProps {
  deploymentID: number | null | undefined;
  deploymentUrl: string;
  componentStatusTree: ComponentStatusTree;
  component: ComponentWithValues | null | undefined;
  onLog?: OnLog;
}

export const ComponentStatusHeader = ({
  deploymentID,
  deploymentUrl,
  componentStatusTree,
  component,
  onLog,
}: ComponentStatusHeaderProps) => {
  const targetPod = componentStatusTree.containers[0] || null;

  const { logPath, handleLog } = useContainerActions({ deploymentID, deploymentUrl, namespace: component?.identifier || '', onLog });

  // FIXME: We have to use two of these hooks now because restart & SSH dont support podless calls yet
  const { copyTooltip, handleRestart, handleSsh, isRestartLoading, isSshLoading } = useContainerActions({
    deploymentID,
    deploymentUrl,
    namespace: component?.identifier || '',
    pod: targetPod?.name,
    onLog,
  });

  return (
    <Stack component="header" horizontal css={headerStyle}>
      <figure css={iconStyle}>
        <ComponentIcon size={24} type={component?.config?.type || 'component'} url={component?.packageUrl || null} hideType />
        <Status itemStateID={componentStatusTree.itemStateID} />
      </figure>
      <Stack component="section" gap={6} css={titleStyle}>
        <ListItemText
          primary={
            <>
              {component?.name || 'Unknown'}
              <ul css={metadataStyle}>
                <Tag type="grey" component="li">
                  {component?.identifier || 'unknown'}
                </Tag>
                {component?.config?.type === 'component' && (
                  <Typography component="li" variant="body2">
                    <FolderSimple /> /{component?.identifier || 'unknown'}
                  </Typography>
                )}
              </ul>
            </>
          }
          // secondary={
          //   <ul css={metadataStyle}>
          //     <Tag type="grey" component="li">
          //       {component?.identifier || 'unknown'}
          //     </Tag>
          //     <Typography component="li" variant="body2">
          //       <FolderSimple /> /{component?.identifier || 'unknown'}
          //     </Typography>
          //   </ul>
          // }
        />
        <Stack component="section" gap={6} horizontal>
          <Link to={logPath}>
            <Button tiny icon={<List weight="bold" />} onClick={handleLog}>
              Logs
            </Button>
          </Link>
          <Button tiny icon={<ArrowClockwise weight="bold" />} loading={isRestartLoading} onClick={handleRestart}>
            Restart
          </Button>
          <Tooltip open={!!copyTooltip} title={copyTooltip} side="right" align="start">
            <div>
              <SshWarningDropdown item="container">
                <Button tiny icon={<Plus weight="bold" />} loading={isSshLoading} onClick={handleSsh}>
                  Connect
                </Button>
              </SshWarningDropdown>
            </div>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );
};

const headerStyle = css`
  .body1 {
    font-weight: bold;
  }

  a {
    text-decoration: none;
  }
`;

const iconStyle = css`
  position: relative;
  display: flex;
  align-self: flex-start;
  margin: 0;

  > span {
    position: absolute;
    right: -6px;
    bottom: -6px;
  }
`;

const titleStyle = css`
  margin-top: -6px;
  flex: 1;
  /* align-items: center; */
  /* justify-content: space-between; */
`;

const metadataStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;

  > li {
    display: flex;
    align-items: center;
    gap: 4px;

    &.tag {
      color: ${theme.color.grey[100]} !important;
    }
  }
`;
